import { useReportTableFilters } from '@views/pages/Reporting/Reports/hooks';
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  OutlinedInput,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { t } from 'i18next';
import { toolbarFieldSx } from 'ui';
import {
  ReportTableColumnSelectionFilter,
  ColumnDensityFilter,
} from 'components/TablePreferences';

export const ReportTableFilters = () => {
  const {
    handleSearchChange,
    search,
    handleSelectFolder,
    folder,
    folderOptions,
    org,
    report_type,
    handleSelectOrg,
    handleSelectType,
    organizationOptions,
    resetFilters,
    typeOptions,
    density,
    handleDensityChange,
  } = useReportTableFilters();

  return (
    <Box
      sx={{
        width: '100%',
        padding: '1rem',
        display: 'flex',
        gap: '8px',
        bgcolor: 'white',
        border: theme => `1px solid ${theme.palette.grey[300]}`,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <OutlinedInput
        id='searchField'
        placeholder={t('reporting.report.filters.search')}
        onChange={handleSearchChange}
        value={search ?? ''}
        sx={{
          ...toolbarFieldSx,
          '& input': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
        startAdornment={
          <InputAdornment sx={{ padding: 0 }} position='start'>
            <SearchIcon />
          </InputAdornment>
        }
      />

      <Autocomplete
        sx={toolbarFieldSx}
        size='medium'
        disablePortal
        options={organizationOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option: { id: string; name: string }) => option.name}
        onChange={handleSelectOrg}
        value={org}
        renderInput={params => (
          <TextField
            {...params}
            label={t('reporting.report.filters.organization')}
            sx={{
              '& input': { textOverflow: 'ellipsis' },
              '& label:not(.MuiInputLabel-shrink)': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        )}
      />

      <Autocomplete
        sx={toolbarFieldSx}
        size='medium'
        disablePortal
        options={typeOptions.map(o => ({ id: o.id, name: o.name }))}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option: { id: string; name: string }) => option.name}
        onChange={handleSelectType}
        value={report_type}
        renderInput={params => (
          <TextField
            {...params}
            label={t('reporting.report.filters.type')}
            sx={{
              '& input': { textOverflow: 'ellipsis' },
              '& label:not(.MuiInputLabel-shrink)': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        )}
      />

      <Autocomplete
        sx={toolbarFieldSx}
        size='medium'
        isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
        getOptionLabel={option => option?.name ?? ''}
        disablePortal
        options={folderOptions}
        onChange={handleSelectFolder}
        value={folder ?? null}
        renderInput={params => (
          <TextField
            {...params}
            label={t('reporting.report.filters.folder')}
            sx={{
              '& input': { textOverflow: 'ellipsis' },
              '& label:not(.MuiInputLabel-shrink)': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        )}
      />

      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Button onClick={resetFilters}>{t('filters.resetShort')}</Button>
      </Box>

      <Box
        sx={{ display: 'flex', gap: '8px', alignItems: 'center', ml: 'auto' }}
      >
        <ReportTableColumnSelectionFilter />
        <ColumnDensityFilter
          handleDensityChange={handleDensityChange}
          selectedValue={density}
        />
      </Box>
    </Box>
  );
};
